import React, { Component } from "react";
import { Link } from "react-router-dom";

import Navbar from "../../Navbar/Navbar";
import MainBodyContainer from "../../Common/Body/MainBodyContainer/MainBodyContainer";
import Footer from "../../Footer/Footer";
import BarkingLogo from "../../../images/logos/logo-barking-dagenham.svg";
import visaClassroom from "../../../images/coding-camp/6C8A6080.JPG";
import visaPresenting from "../../../images/coding-camp/6C8A6089.JPG";
import visaMentoring from "../../../images/coding-camp/6C8A6095.JPG";
import visaDemo from "../../../images/coding-camp/6C8A6108.JPG";
import visaGroupIndoor from "../../../images/coding-camp/6C8A6145.jpg";
import visaGroupOutdoor from "../../../images/coding-camp/6C8A6158.JPG";
import "./NCILYouthCodingCamp.css";

const learningAreas = [
  ["01", "Code with purpose", "Build confidence with Python, problem-solving and the foundations behind every digital product."],
  ["02", "Make things people can use", "Turn ideas into websites and apps, learning how thoughtful design and clean code work together."],
  ["03", "Bring it to life", "Explore APIs, deployment and AI concepts through projects made to be shared, tested and improved."],
];

const testimonials = [
  {
    quote: "I never thought I could build a website, but by week 7 I made my own portfolio page!",
    name: "Jayden Smith",
    detail: "14 years old · Dagenham",
    initials: "JS",
  },
  {
    quote: "My son came home every week excited to show us what he built. Now he wants to be a software engineer!",
    name: "Sarah Johnson",
    detail: "Parent · Barking",
    initials: "SJ",
  },
];

class NCILYouthCodingCamp extends Component {
  constructor(props) {
    super(props);
    this.state = { activeTestimonial: 0, displayedText: "", hasStartedTyping: false };
    this.terminalRef = React.createRef();
    this.typingTimer = null;
    this.observer = null;
  }

  componentDidMount() {
    if (window.location.hash === "#visa-career-day") {
      window.setTimeout(() => {
        const section = document.getElementById("visa-career-day");
        if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    }

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.startTypewriter();
        this.observer.disconnect();
      }
    }, { threshold: 0.35 });

    if (this.terminalRef.current) this.observer.observe(this.terminalRef.current);
  }

  componentWillUnmount() {
    window.clearInterval(this.typingTimer);
    if (this.observer) this.observer.disconnect();
  }

  startTypewriter = () => {
    if (this.state.hasStartedTyping) return;
    this.setState({ hasStartedTyping: true });
    const quote = testimonials[this.state.activeTestimonial].quote;
    let index = 0;
    this.typingTimer = window.setInterval(() => {
      index += 1;
      this.setState({ displayedText: quote.slice(0, index) });
      if (index >= quote.length) window.clearInterval(this.typingTimer);
    }, 32);
  };

  selectTestimonial = (activeTestimonial) => {
    window.clearInterval(this.typingTimer);
    const quote = testimonials[activeTestimonial].quote;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.setState({
      activeTestimonial,
      displayedText: reducedMotion ? quote : "",
      hasStartedTyping: true,
    });
    if (reducedMotion) return;

    let index = 0;
    this.typingTimer = window.setInterval(() => {
      index += 1;
      this.setState({ displayedText: quote.slice(0, index) });
      if (index >= quote.length) window.clearInterval(this.typingTimer);
    }, 32);
  };

  render() {
    const testimonial = testimonials[this.state.activeTestimonial];

    return (
      <div className="coding-camp-page">
        <Navbar />
        <MainBodyContainer heading={undefined}>
          <section className="camp-hero" aria-labelledby="coding-camp-title">
            <div className="camp-hero-copy">
              <p className="camp-kicker">Young People Coding & AI Camp</p>
              <h1 id="coding-camp-title">Make work<br />worth showing.</h1>
              <p className="camp-hero-intro">
                A free coding programme for young people aged 10–18 in Barking &amp; Dagenham.
                Learn by building, then share what you have made.
              </p>
              <div className="camp-hero-actions">
                <Link to="/contact?inquiry=coding-camp-registration" className="camp-button camp-button-light">Register a young person</Link>
                <a href="#visa-career-day" className="camp-text-link">See the visit</a>
              </div>
              <div className="camp-hero-details">
                <span>For ages 10–18</span><span>Free to join</span><span>No experience needed</span>
              </div>
            </div>
            <figure className="camp-hero-image">
              <img src={visaPresenting} alt="Young person presenting work during a visit to Visa Europe HQ" />
              <figcaption>Presenting work beyond the classroom.</figcaption>
            </figure>
          </section>

          <section className="camp-proposition" aria-labelledby="camp-proposition-title">
            <div className="camp-proposition-copy">
              <p className="camp-kicker camp-kicker-dark">Not another after-school club</p>
              <h2 id="camp-proposition-title">A place to start making.</h2>
            </div>
            <div className="camp-proposition-text">
              <p>Young people come with curiosity. They leave with projects, new language for their ideas, and the confidence to explain how they made something work.</p>
              <p>The programme is funded by Barking &amp; Dagenham’s Neighbourhood Community Infrastructure Levy fund, so there is no cost to families.</p>
              <div className="camp-funder"><span>Funded by</span><img src={BarkingLogo} alt="Barking and Dagenham Council" /></div>
            </div>
          </section>

          <section className="camp-learning" aria-labelledby="learning-title">
            <div className="camp-section-heading">
              <p className="camp-kicker camp-kicker-dark">What we make space for</p>
              <h2 id="learning-title">From first line<br />to finished project.</h2>
            </div>
            <div className="learning-list">
              {learningAreas.map(([number, title, description]) => (
                <article className="learning-item" key={number}>
                  <span>{number}</span><h3>{title}</h3><p>{description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="visa-story" id="visa-career-day" aria-labelledby="visa-story-title">
            <div className="visa-story-heading">
              <p className="camp-kicker">Beyond the classroom</p>
              <h2 id="visa-story-title">Their work<br />had a room.</h2>
              <p>Our young people visited Visa Europe HQ, where they shared their projects, listened, asked questions and saw their skills in a new setting.</p>
            </div>
            <div className="visa-story-grid">
              <figure className="visa-story-main">
                <img src={visaMentoring} alt="Young people discussing their work with visitors during the Visa Europe HQ visit" loading="lazy" />
                <figcaption>Ideas get stronger in conversation.</figcaption>
              </figure>
              <figure className="visa-story-small visa-story-small-top">
                <img src={visaClassroom} alt="Young people gathered for a session during their visit to Visa Europe HQ" loading="lazy" />
              </figure>
              <figure className="visa-story-small visa-story-small-bottom">
                <img src={visaDemo} alt="Young person showing a project on a laptop during the Visa Europe HQ visit" loading="lazy" />
              </figure>
            </div>
            <figure className="visa-story-closing">
              <img src={visaGroupOutdoor} alt="Coding Camp cohort gathered outside Visa Europe HQ" loading="lazy" />
              <figcaption>A day to remember what is possible.</figcaption>
            </figure>
          </section>

          <section className="camp-testimonials" ref={this.terminalRef} aria-labelledby="testimonial-title">
            <div className="camp-testimonial-heading">
              <p className="camp-kicker">From our community</p>
              <h2 id="testimonial-title">The good kind<br />of feedback.</h2>
            </div>
            <div className="terminal-testimonial">
              <div className="terminal-chrome"><span></span><span></span><span></span><p>camp-feedback.txt</p></div>
              <div className="terminal-copy"><span className="terminal-prompt">&gt;</span><p>“{this.state.displayedText}<span className="terminal-cursor" aria-hidden="true">▍</span>”</p></div>
              <div className="terminal-author"><strong>{testimonial.initials}</strong><div><span>{testimonial.name}</span><small>{testimonial.detail}</small></div></div>
              <div className="terminal-nav" aria-label="Choose a testimonial">
                {testimonials.map((item, index) => <button key={item.name} className={index === this.state.activeTestimonial ? "active" : ""} onClick={() => this.selectTestimonial(index)} aria-label={`Show testimonial from ${item.name}`} />)}
              </div>
            </div>
          </section>

          <section className="camp-next-step" aria-labelledby="next-step-title">
            <div className="camp-next-step-image"><img src={visaGroupIndoor} alt="Coding Camp cohort together inside Visa Europe HQ" loading="lazy" /></div>
            <div className="camp-next-step-content">
              <p className="camp-kicker camp-kicker-dark">The next opportunity starts here</p>
              <h2 id="next-step-title">Let’s make room<br />for more.</h2>
              <p>Whether you are a parent looking for a place to begin, or an organisation ready to open up new possibilities, we would love to hear from you.</p>
              <div className="camp-dual-actions">
                <Link to="/contact?inquiry=coding-camp-registration" className="camp-button camp-button-dark">Register a young person</Link>
                <Link to="/contact?inquiry=coding-camp-partnership" className="camp-button camp-button-outline">Talk about partnership</Link>
              </div>
            </div>
          </section>
        </MainBodyContainer>
        <Footer />
      </div>
    );
  }
}

export default NCILYouthCodingCamp;
